import { toFloat } from '@/models/Utils'
import { EditCreateFields } from '../observation'
import { WellObservation, wellObservationApolloModel, WellObservationParams } from './WellObservation'

export const beamPumpProgCavityApolloModel = wellObservationApolloModel(
  ` casingPressure
    tubingPressure
    waterVolume
    runTimePercent
    strokesPerMinute
    pumpOffs
    smartTime
    gasVolume
    hpsd
    onCycle
    offCycle`
)

export const beamPumpProgCavityFields : EditCreateFields[] = [
  {
    name: 'casingPressure',
    title: 'Casing Pressure',
    type: 'decimal',
    limits: [6, 2]
  },
  {
    name: 'tubingPressure',
    title: 'Tubing Pressure',
    type: 'decimal',
    limits: [6, 2]
  },
  {
    name: 'runTimePercent',
    title: 'Run Time Percent',
    type: 'decimal',
    limits: [3, 2],
    required: false
  },
  {
    name: 'pumpOffs',
    title: 'Pump Offs',
    type: 'decimal',
    limits: [6, 2],
    required: false
  },
  {
    name: 'smartTime',
    title: 'Smart Time',
    type: 'decimal',
    limits: [6, 2],
    required: false
  },
  {
    name: 'waterVolume',
    title: 'Water Volume',
    type: 'decimal',
    limits: [10, 4],
    unit: 'Water',
    required: false
  },
  {
    name: 'gasVolume',
    title: 'Gas Volume',
    type: 'decimal',
    limits: [10, 4],
    unit: 'Gas',
    required: false,
    tooltip: 'Gas values are recorded at the meter’s pressure base.'
  },
  {
    name: 'hpsd',
    title: 'High Pressure Shut Down',
    type: 'decimal',
    limits: [6, 2],
    required: false
  },
  {
    name: 'strokesPerMinute',
    title: 'Strokes Per Minute',
    type: 'decimal',
    limits: [6, 2],
    required: false
  },
  {
    name: 'onCycle',
    title: 'On Cycle',
    type: 'decimal',
    limits: [6, 2]
  },
  {
    name: 'offCycle',
    title: 'Off Cycle',
    type: 'decimal',
    limits: [6, 2]
  }
]

export interface BeamPumpProgCavityParams extends WellObservationParams {
  casingPressure?: number | null
  tubingPressure?: number | null
  waterVolume?: number | null
  runTimePercent?: number | null
  strokesPerMinute?: number | null
  pumpOffs?: number | null
  smartTime?: number | null
  gasVolume?: number | null
  hpsd?: number | null
  onCycle?: number | null
  offCycle?: number | null
}

export class BeamPumpProgCavity extends WellObservation {
  private _casingPressure: number | null = null
  private _tubingPressure: number | null = null
  private _waterVolume: number | null = null
  private _runTimePercent: number | null = null
  private _strokesPerMinute: number | null = null
  private _pumpOffs: number | null = null
  private _smartTime: number | null = null
  private _gasVolume: number | null = null
  private _hpsd: number | null = null
  private _onCycle: number | null = null
  private _offCycle: number | null = null

  constructor (fieldObservation?: BeamPumpProgCavityParams) {
    super(fieldObservation)

    if (fieldObservation !== undefined) {
      if (fieldObservation.casingPressure !== undefined) {
        this._casingPressure = fieldObservation.casingPressure
      }
      if (fieldObservation.tubingPressure !== undefined) {
        this._tubingPressure = fieldObservation.tubingPressure
      }
      if (fieldObservation.waterVolume !== undefined) {
        this._waterVolume = fieldObservation.waterVolume
      }
      if (fieldObservation.runTimePercent !== undefined) {
        this._runTimePercent = fieldObservation.runTimePercent
      }
      if (fieldObservation.strokesPerMinute !== undefined) {
        this._strokesPerMinute = fieldObservation.strokesPerMinute
      }
      if (fieldObservation.pumpOffs !== undefined) {
        this._pumpOffs = fieldObservation.pumpOffs
      }
      if (fieldObservation.smartTime !== undefined) {
        this._smartTime = fieldObservation.smartTime
      }
      if (fieldObservation.gasVolume !== undefined) {
        this._gasVolume = fieldObservation.gasVolume
      }
      if (fieldObservation.hpsd !== undefined) {
        this._hpsd = fieldObservation.hpsd
      }
      if (fieldObservation.onCycle !== undefined) {
        this._onCycle = fieldObservation.onCycle
      }
      if (fieldObservation.offCycle !== undefined) {
        this._offCycle = fieldObservation.offCycle
      }
    }
  }

  get casingPressure (): number | null {
    return this._casingPressure
  }

  set casingPressure (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._casingPressure = value
  }

  get tubingPressure (): number | null {
    return this._tubingPressure
  }

  set tubingPressure (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._tubingPressure = value
  }

  get waterVolume (): number | null {
    return this._waterVolume
  }

  set waterVolume (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._waterVolume = value
  }

  get runTimePercent (): number | null {
    return this._runTimePercent
  }

  set runTimePercent (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._runTimePercent = value
  }

  get strokesPerMinute (): number | null {
    return this._strokesPerMinute
  }

  set strokesPerMinute (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._strokesPerMinute = value
  }

  get pumpOffs (): number | null {
    return this._pumpOffs
  }

  set pumpOffs (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._pumpOffs = value
  }

  get smartTime (): number | null {
    return this._smartTime
  }

  set smartTime (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._smartTime = value
  }

  get gasVolume (): number | null {
    return this._gasVolume
  }

  set gasVolume (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._gasVolume = value
  }

  get hpsd (): number | null {
    return this._hpsd
  }

  set hpsd (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._hpsd = value
  }

  get onCycle (): number | null {
    return this._onCycle
  }

  set onCycle (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._onCycle = value
  }

  get offCycle (): number | null {
    return this._offCycle
  }

  set offCycle (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._offCycle = value
  }

  getApolloModel () {
    return {
      casingPressure: toFloat(this.casingPressure),
      tubingPressure: toFloat(this.tubingPressure),
      waterVolume: toFloat(this.waterVolume),
      runTimePercent: toFloat(this.runTimePercent),
      strokesPerMinute: toFloat(this.strokesPerMinute),
      pumpOffs: toFloat(this.pumpOffs),
      smartTime: toFloat(this.smartTime),
      gasVolume: toFloat(this.gasVolume),
      hpsd: toFloat(this.hpsd),
      onCycle: toFloat(this.onCycle),
      offCycle: toFloat(this.offCycle)
    }
  }

  getApolloFormattedModel () {
    return {
      ...super.getApolloFormattedModel(),
      ...this.getApolloModel()
    }
  }

  getApolloFormattedModelForEdit () {
    return {
      ...super.getApolloFormattedModelForEdit(),
      ...this.getApolloModel()
    }
  }
}
